import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getRouteSeo, SITE_NAME, SITE_URL } from "@/lib/seoConfig";

/**
 * RouteSEO — single source of SEO truth.
 * Mounted once near the top of the app; reads the current route and sets:
 *   - <title>
 *   - <meta name="description">
 *   - <link rel="canonical"> (pointing to the non-www version)
 *   - Open Graph + Twitter card tags
 */
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "tag" || v == null) return;
    el.setAttribute(k, v);
  });
  return el;
}

export default function RouteSEO() {
  const location = useLocation();
  const path = location.pathname || "/";

  useEffect(() => {
    const seo = getRouteSeo(path);
    const title = seo.title || `${SITE_NAME} | Trash Pickup Service`;
    const desc =
      seo.description ||
      "Bag Dash Kids — youth-led, parent-supervised trash bag pickup on Friday and Saturday. Safe, friendly, mobile-first scheduling.";
    const canonical = `${SITE_URL}${path === "/" ? "" : path}`;

    document.title = title;
    upsertMeta('meta[name="description"]', { tag: "meta", name: "description", content: desc });
    upsertMeta('link[rel="canonical"]', { tag: "link", rel: "canonical", href: canonical });

    // Open Graph
    upsertMeta('meta[property="og:title"]', { tag: "meta", property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { tag: "meta", property: "og:description", content: desc });
    upsertMeta('meta[property="og:type"]', { tag: "meta", property: "og:type", content: "website" });
    upsertMeta('meta[property="og:url"]', { tag: "meta", property: "og:url", content: canonical });
    upsertMeta('meta[property="og:site_name"]', { tag: "meta", property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:image"]', { tag: "meta", property: "og:image", content: `${SITE_URL}/icon-512.png` });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { tag: "meta", name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { tag: "meta", name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { tag: "meta", name: "twitter:description", content: desc });
    upsertMeta('meta[name="twitter:image"]', { tag: "meta", name: "twitter:image", content: `${SITE_URL}/icon-512.png` });
  }, [path]);

  return null;
}
